const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'http://localhost:3002';

export interface AuthUser {
  id: string;
  name: string;
  email?: string | null;
  avatar_url?: string | null;
}

// Token is kept in memory only. Authentication APIs use an httpOnly cookie,
// so the frontend never needs to read it. The in-memory copy is used for the
// WebSocket authenticate event, which cannot carry cookies automatically.
let memoryToken: string | null = null;

export function getStoredToken(): string | null {
  return memoryToken;
}

export function setStoredToken(token: string) {
  memoryToken = token;
}

export function clearStoredToken() {
  memoryToken = null;
}

export async function verifyStoredToken(): Promise<{ user: AuthUser; token: string } | null> {
  try {
    const res = await fetch(`${AUTH_URL}/auth/me`, {
      credentials: 'include',
    });
    if (!res.ok) {
      clearStoredToken();
      return null;
    }
    const data = await res.json();
    if (data.token) {
      setStoredToken(data.token);
    }
    return { user: data.user as AuthUser, token: data.token as string };
  } catch {
    return null;
  }
}

export async function exchangeGoogleToken(idToken: string): Promise<{ user: AuthUser; token: string } | null> {
  try {
    const res = await fetch(`${AUTH_URL}/auth/verify/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token: idToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.token) {
      setStoredToken(data.token);
    }
    return { user: data.user as AuthUser, token: data.token as string };
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${AUTH_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // ignore
  } finally {
    clearStoredToken();
  }
}

export function getAuthPayload(): { userId: string; userName: string; email?: string; avatarUrl?: string | null; provider: string } | null {
  const token = getStoredToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.userId as string,
      userName: payload.userName as string,
      email: payload.email as string | undefined,
      avatarUrl: payload.avatarUrl as string | null | undefined,
      provider: payload.provider as string,
    };
  } catch {
    return null;
  }
}
